/* eslint-disable import/no-cycle */
import { getBudgetById } from '_/modules/budget/actions'
import React, { createContext, useState, useContext, useMemo } from 'react'
import { useDispatch } from 'react-redux'

/**
 * @typedef {'default' | 'add-service'} BudgetFormPage
 */

/**
 * @typedef {Object} BudgetFormContextType
 * @property {BudgetFormPage} page
 * @property {(page: BudgetFormPage) => void} setPage
 * @property {boolean} loading
 * @property {(loading: boolean) => void} setLoading
 * @property {boolean} isEditing
 * @property {(isEditing: boolean) => void} setIsEditing
 * @property {Object} budget
 * @property {(budget: Object) => void} setBudget
 * @property {boolean} resetForm
 * @property {(resetForm: boolean) => void} setResetForm
 * @property {Object} budgetResolutionToEdit
 * @property {(budgetResolutionToEdit: Object) => void} setBudgetResolutionToEdit
 * @property {(budgetId: string) => void} fetchBudget
 * @property {boolean} addProblemReadOnly
 * @property {(addProblemReadOnly: boolean) => void} setAddProblemReadOnly
 */

/** @type {React.Context<BudgetFormContextType | undefined>} */
const BudgetFormContext = createContext(undefined)

/**
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */

function BudgetFormProvider({ children }) {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [page, setPage] = useState('default')
  const [budget, setBudget] = useState(null)
  const [budgetResolutionToEdit, setBudgetResolutionToEdit] = useState(null)
  const [resetForm, setResetForm] = useState(false)
  const [addProblemReadOnly, setAddProblemReadOnly] = useState(false)

  async function fetchBudget(budgetId) {
    const params = { budgetView: true, showOriginalBudget: false }
    await dispatch(getBudgetById(budgetId, params)).then(budgetResponse =>
      setBudget(budgetResponse)
    )
  }

  const contextData = useMemo(
    () => ({
      page,
      setPage,
      loading,
      setLoading,
      isEditing,
      setIsEditing,
      budget,
      setBudget,
      resetForm,
      setResetForm,
      budgetResolutionToEdit,
      setBudgetResolutionToEdit,
      fetchBudget,
      addProblemReadOnly,
      setAddProblemReadOnly,
    }),
    [
      loading,
      isEditing,
      setIsEditing,
      setLoading,
      page,
      setPage,
      budget,
      setBudget,
      resetForm,
      setResetForm,
      budgetResolutionToEdit,
      setBudgetResolutionToEdit,
      fetchBudget,
      addProblemReadOnly,
      setAddProblemReadOnly,
    ]
  )

  return <BudgetFormContext.Provider value={contextData}>{children}</BudgetFormContext.Provider>
}

function useBudgetForm() {
  const context = useContext(BudgetFormContext)

  if (!context) throw new Error('useBudgetForm must be used within a BudgetFormProvider')
  return context
}

export { BudgetFormProvider, useBudgetForm }
